import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    ProtoApiService,
    ProtoRequest,
    ProtoResponse
} from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { CurrencyCode, Error, Money } from './models';

@Injectable({ providedIn: 'root' })
export class AcceptOfferService extends ProtoApiService<AcceptOfferRequest, AcceptOfferResponse> {
    public requestType = AcceptOfferRequest;
    public responseType = AcceptOfferResponse;
    public endpointName = 'v1/acceptOffer';
    public serviceName = 'fundingApi';
    public usePascalForUrl = false;
    public addApi = false;

    public constructor(_http: HttpClient, @Inject(SETTINGS) _settings: ISettings) {
        super(_http, _settings);
    }
    public requestMapper(req: AcceptOfferRequest): AcceptOfferRequest {
        return req;
    }

    public responseMapper(res: AcceptOfferResponse): AcceptOfferResponse {
        return res;
    }

    public get endpointUrl(): string {
        return super.endpointUrl.replace('/api', '');
    }
}

export class AcceptOfferRequest extends ProtoRequest {
    public bankAccountKey: string;
    public effectiveFundingDate: string;
    public isPayLater: boolean;
    public payablesRequest: PayableRequest[];
    public requestDate: string;

    constructor(obj?: {
        bankAccountKey: string;
        effectiveFundingDate: string;
        isPayLater: boolean;
        payablesRequest: PayableRequest[];
    }) {
        super();
        if (obj != null) {
            this.bankAccountKey =
                typeof obj.bankAccountKey === 'undefined'
                    ? this.bankAccountKey
                    : obj.bankAccountKey;
            this.effectiveFundingDate =
                typeof obj.effectiveFundingDate === 'undefined'
                    ? this.effectiveFundingDate
                    : obj.effectiveFundingDate;
            this.isPayLater =
                typeof obj.isPayLater === 'undefined' ? this.isPayLater : obj.isPayLater;
            this.payablesRequest =
                typeof obj.payablesRequest === 'undefined'
                    ? this.payablesRequest
                    : obj.payablesRequest;
        }
    }
}

export class PayableRequest extends ProtoResponse {
    public id: number;

    constructor(obj?: { id?: number }) {
        super();
        if (obj != null) {
            this.id = typeof obj.id === 'undefined' ? this.id : obj.id;
        }
    }
}

export class AcceptOfferResponse extends ProtoResponse {
    public onTermsFundingAmountTotal: Money;
    public earlyPaidAmountTotal: Money;
    public expectedAmountDueTotal: Money;
    public expectedPayLaterTotal: Money;
    public payableAmountTotal: Money;
    public estimatedAmountDueTotal: Money;
    public estimatedPayLaterFeesTotal: Money;
    public totalApprovals: number;
    public totalPayables: number;
    public totalPayablesRejected: number;
    public totalInvoicesEarlyPaid: number;
    public rejectedAmountTotal: Money;
    public responseStatus: 'SUCCESSFUL' | 'PARTIALLY_SUCCESSFUL' | 'NOT_SUCCESSFUL';
    public error: Error;

    constructor(obj?: {
        onTermsFundingAmountTotal?: IMoney;
        earlyPaidAmountTotal?: IMoney;
        expectedAmountDueTotal?: IMoney;
        expectedPayLaterTotal?: IMoney;
        payableAmountTotal?: IMoney;
        estimatedAmountDueTotal?: IMoney;
        estimatedPayLaterFeesTotal?: IMoney;
        totalApprovals?: number;
        totalPayables?: number;
        totalInvoicesEarlyPaid?: number;
        totalPayablesRejected?: number;
        rejectedAmountTotal?: IMoney;
        responseStatus?: 'SUCCESSFUL' | 'PARTIALLY_SUCCESSFUL' | 'NOT_SUCCESSFUL';
        error?: Error;
    }) {
        super();
        if (obj != null) {
            const oneofSets = [['availableAmountTotal', 'error']];
            oneofSets.forEach((oneof) => {
                const keysAssigned = oneof.filter((key) => typeof obj[key] !== 'undefined');
                const shouldThrow = keysAssigned.length > 1;
                if (shouldThrow) {
                    throw new (window as any).Error(
                        'Attempted to assign more than one property from a "oneof" definition.\n' +
                            'May assign one of ' +
                            oneof.join(' or ') +
                            '.\n' +
                            'Attempted to assign ' +
                            keysAssigned.join(' and ') +
                            '.'
                    );
                }
            });

            this.onTermsFundingAmountTotal =
                typeof obj.onTermsFundingAmountTotal === 'undefined'
                    ? this.onTermsFundingAmountTotal
                    : this.buildMoney(obj.onTermsFundingAmountTotal);
            this.earlyPaidAmountTotal =
                typeof obj.earlyPaidAmountTotal === 'undefined'
                    ? this.earlyPaidAmountTotal
                    : this.buildMoney(obj.earlyPaidAmountTotal);
            this.expectedAmountDueTotal =
                typeof obj.expectedAmountDueTotal === 'undefined'
                    ? this.expectedAmountDueTotal
                    : this.buildMoney(obj.expectedAmountDueTotal);
            this.expectedPayLaterTotal =
                typeof obj.expectedPayLaterTotal === 'undefined'
                    ? this.expectedPayLaterTotal
                    : this.buildMoney(obj.expectedPayLaterTotal);
            this.payableAmountTotal =
                typeof obj.payableAmountTotal === 'undefined'
                    ? this.payableAmountTotal
                    : this.buildMoney(obj.payableAmountTotal);
            this.estimatedAmountDueTotal =
                typeof obj.estimatedAmountDueTotal === 'undefined'
                    ? this.estimatedAmountDueTotal
                    : this.buildMoney(obj.estimatedAmountDueTotal);
            this.estimatedPayLaterFeesTotal =
                typeof obj.estimatedPayLaterFeesTotal === 'undefined'
                    ? this.estimatedPayLaterFeesTotal
                    : this.buildMoney(obj.estimatedPayLaterFeesTotal);
            this.rejectedAmountTotal =
                typeof obj.rejectedAmountTotal === 'undefined'
                    ? this.rejectedAmountTotal
                    : this.buildMoney(obj.rejectedAmountTotal);
            this.totalApprovals =
                typeof obj.totalApprovals === 'undefined'
                    ? this.totalApprovals
                    : obj.totalApprovals;
            this.totalPayablesRejected =
                typeof obj.totalPayablesRejected === 'undefined'
                    ? this.totalPayablesRejected
                    : obj.totalPayablesRejected;
            this.totalInvoicesEarlyPaid =
                typeof obj.totalInvoicesEarlyPaid === 'undefined'
                    ? this.totalInvoicesEarlyPaid
                    : obj.totalInvoicesEarlyPaid;
            this.responseStatus =
                typeof obj.responseStatus === 'undefined'
                    ? this.responseStatus
                    : obj.responseStatus;
            this.totalPayables =
                typeof obj.totalPayables === 'undefined' ? this.totalPayables : obj.totalPayables;
            this.error = typeof obj.error === 'undefined' ? this.error : obj.error;
        }
    }

    private buildMoney(value: IMoney): Money {
        return new Money({
            cents: value.amountCents ? value.amountCents : 0,
            currencyCode: value.currencyCode
        });
    }
}

export interface IMoney {
    amountCents: number;
    currencyCode: CurrencyCode;
}
